/**
 * Thin, defensive wrappers around Web Storage. Storage can throw (private mode,
 * quota, disabled) and JSON can be corrupt: callers get `null` instead of an
 * exception in both cases.
 */

type WebStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem' | 'clear'>;

const safe = (storage: () => WebStorage) => ({
  get(key: string): string | null {
    try {
      return storage().getItem(key);
    } catch {
      return null;
    }
  },
  getJson<T>(key: string): T | null {
    const raw = this.get(key);
    if (raw === null) return null;
    try {
      return JSON.parse(raw) as T;
    } catch {
      return null;
    }
  },
  set(key: string, value: string): void {
    try {
      storage().setItem(key, value);
    } catch {
      /* storage unavailable: the app keeps working in memory */
    }
  },
  setJson(key: string, value: unknown): void {
    this.set(key, JSON.stringify(value));
  },
  remove(key: string): void {
    try {
      storage().removeItem(key);
    } catch {
      /* ignore */
    }
  },
  clear(): void {
    try {
      storage().clear();
    } catch {
      /* ignore */
    }
  },
});

export const local = safe(() => window.localStorage);
export const session = safe(() => window.sessionStorage);
